import React, { useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation, gql } from '@apollo/client';
import { EditWidgetFormFields } from './EditWidgetFormFields';
import { SourceField } from '../types';
import {
  FieldType,
  ChartType,
  Aggregation,
  chartTypeOptions,
  aggregationOptions,
} from './widgetOptions';

const UPDATE_DASHBOARD_WIDGET = gql`
  mutation UpdateDashboardWidget($id: ID!, $input: DashboardWidgetInput!) {
    updateDashboardWidget(id: $id, input: $input) {
      id
      title
      fieldId
      sectionId
      chartType
      aggregation
      layout {
        x
        y
        w
        h
        minW
        minH
      }
      visible
      orderIndex
    }
  }
`;

interface EditWidgetFormValues {
  title: string;
  fieldId: string;
  chartType: ChartType;
  aggregation: Aggregation;
}

export interface EditableWidget {
  id: string;
  title: string;
  fieldId: string;
  chartType: ChartType;
  aggregation: Aggregation;
  layout: { x: number; y: number; w: number; h: number; minW: number; minH: number };
}

interface EditWidgetDialogProps {
  open: boolean;
  widget: EditableWidget | null;
  sourceFields: SourceField[];
  onSaved?: () => void;
  onCancel: () => void;
}

export const EditWidgetDialog: React.FC<EditWidgetDialogProps> = ({
  open,
  widget,
  sourceFields,
  onSaved,
  onCancel,
}) => {
  const [updateWidget] = useMutation(UPDATE_DASHBOARD_WIDGET, {
    refetchQueries: ['GetDashboardWidgets', 'GetDashboardWidgetsAndSections'],
  });

  const methods = useForm<EditWidgetFormValues>({
    defaultValues: { title: '', fieldId: '', chartType: 'LINE', aggregation: 'LATEST' },
  });

  const { control, watch, setValue, reset, handleSubmit } = methods;
  const selectedFieldId = watch('fieldId');

  const selectedField = useMemo(
    () => sourceFields.find((f) => f.fieldId === selectedFieldId),
    [sourceFields, selectedFieldId]
  );
  const fieldType: FieldType | undefined = selectedField?.fieldType;

  useEffect(() => {
    if (widget && open) {
      reset({
        title: widget.title,
        fieldId: widget.fieldId,
        chartType: widget.chartType,
        aggregation: widget.aggregation,
      });
    }
  }, [widget, open, reset]);

  const handleFieldChange = (newFieldId: string) => {
    const field = sourceFields.find((f) => f.fieldId === newFieldId);
    if (field) {
      setValue('fieldId', newFieldId);
      setValue('chartType', chartTypeOptions(field.fieldType)[0].value as ChartType);
      setValue('aggregation', aggregationOptions(field.fieldType)[0].value as Aggregation);
    }
  };

  const onSubmit = (values: EditWidgetFormValues) => {
    if (!widget) return;
    onSaved?.();
    updateWidget({
      variables: {
        id: widget.id,
        input: {
          title: values.title,
          fieldId: values.fieldId,
          chartType: values.chartType,
          aggregation: values.aggregation,
          layout: widget.layout,
        },
      },
      optimisticResponse: {
        updateDashboardWidget: {
          __typename: 'DashboardWidget',
          id: widget.id,
          title: values.title,
          fieldId: values.fieldId,
          sectionId: null,
          chartType: values.chartType,
          aggregation: values.aggregation,
          layout: widget.layout,
          visible: true,
          orderIndex: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      },
    }).catch((err) => {
      console.error('Update widget failed:', err);
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Widget</DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <EditWidgetFormFields
              sourceFields={sourceFields}
              fieldType={fieldType}
              onFieldChange={handleFieldChange}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default EditWidgetDialog;
